import _ from 'lodash';
import {
  SHIFT_OPTIONS,
  SCHEDULE_OPTIONS,
  YES_NO_OPTIONS,
  MIN_CLEARANCE_OPTIONS,
  POLYGRAPH_TYPE_OPTIONS,
  EDUCATION_REQUIRED_OPTIONS,
  FILLED_CLOSED_OPTIONS,
} from '../constants/jobConstants';
/**
 * name: The name of the property defined in JIRA seen when requesting getProcurementRequestIssueType / getRipProjectFieldsMeta
 * validationType: The field type. If the field is going to be validated by yup schemas, this property should equal a valid yup type
 * validations: an array of additional validations for the field. If using yup to validate the model, use validations supported by yup
 */

const formatSelectOptions = (options) => {
  return options.map((option) => {
    return { text: option, value: option };
  });
};

const JobModel = _.mapValues(
  {
    jobId: {
      name: 'Job ID',
      summary: 'System Generated',
      validationType: 'string',
    },
    jobTitle: {
      name: 'Job Title',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
        {
          type: 'max',
          params: [50, 'Max size is 50 characters'],
        },
      ],
    },
    jobLocation: {
      name: 'Job Location',
      validationType: 'string',
      validations: [
        {
          type: 'max',
          params: [100, 'Max size is 100 characters'],
        },
      ],
    },
    minimumClearance: {
      name: 'Minimum Clearance',
      validationType: 'string',
      options: formatSelectOptions(MIN_CLEARANCE_OPTIONS),
    },
    polygraphType: {
      name: 'Polygraph Type',
      validationType: 'string',
      options: formatSelectOptions(POLYGRAPH_TYPE_OPTIONS),
    },
    daysOpen: { name: 'Days Open' },
    isOpen: { name: 'Is Open', validationType: 'string' },
    postingDate: {
      name: 'Posting Date',
      validationType: 'string',
    },
    closedFilledDate: {
      name: 'Closed/Filled Date',
      validationType: 'string',
    },
    closedFilled: {
      name: 'Closed/Filled',
      validationType: 'string',
      options: formatSelectOptions(FILLED_CLOSED_OPTIONS),
    },
    hourlyRate: {
      name: 'Hourly Rate',
      validationType: 'number',
      validations: [
        {
          type: 'min',
          params: [0, 'Valid value is from $0 - $100,000,000'],
        },
        {
          type: 'max',
          params: [100000000, 'Valid value is from $0 - $100,000,000'],
        },
      ],
    },
    shift: {
      name: 'Shift',
      validationType: 'string',
      options: formatSelectOptions(SHIFT_OPTIONS),
    },
    schedule: {
      name: 'Schedule',
      validationType: 'string',
      options: formatSelectOptions(SCHEDULE_OPTIONS),
    },
    teleworking: {
      name: 'Teleworking',
      validationType: 'string',
      options: formatSelectOptions(YES_NO_OPTIONS),
    },
    usCitizenshipRequired: {
      name: 'US Citizenship Required',
      validationType: 'string',
      options: formatSelectOptions(YES_NO_OPTIONS),
    },
    travel: {
      name: 'Travel',
      validationType: 'string',
      options: formatSelectOptions(YES_NO_OPTIONS),
    },
    travelPercentage: {
      name: 'Travel Percentage',
      validationType: 'number',
      validations: [
        {
          type: 'integer',
        },
        {
          type: 'min',
          params: [0, 'Valid value is from 0% - 100%'],
        },
        {
          type: 'max',
          params: [100, 'Valid value is from 0% - 100%'],
        },
      ],
    },
    educationRequired: {
      name: 'Education Required',
      validationType: 'string',
      options: formatSelectOptions(EDUCATION_REQUIRED_OPTIONS),
    },
    majorSpecialization: {
      name: 'Major/Specialization',
      validationType: 'string',
      validations: [
        {
          type: 'max',
          params: [50, 'Max size is 50 characters'],
        },
      ],
    },
    jobDescription: {
      name: 'Job Description',
      validationType: 'string',
      validations: [
        {
          type: 'required',
        },
        {
          type: 'max',
          params: [5000, 'Max size is 5000 characters'],
        },
      ],
    },
    numberApplicants: {
      name: '# Applicants',
      validationType: 'number',
      validations: [
        {
          type: 'integer',
        },
        {
          type: 'min',
          params: [0, 'Valid value is from 0 - 1000'],
        },
        {
          type: 'max',
          params: [1000, 'Valid value is from 0 - 1000'],
        },
      ],
    },
    numberApplicantsInterviewed: {
      name: '# Applicants Interviewed',
      validationType: 'number',
      validations: [
        {
          type: 'integer',
        },
        {
          type: 'min',
          params: [0, 'Valid value is from 0 - 1000'],
        },
        {
          type: 'max',
          params: [1000, 'Valid value is from 0 - 1000'],
        },
      ],
    },
    numberOffersMade: {
      name: '# Offers Made',
      validationType: 'number',
      validations: [
        {
          type: 'integer',
        },
        {
          type: 'min',
          params: [0, 'Valid value is from 0 - 1000'],
        },
        {
          type: 'max',
          params: [1000, 'Valid value is from 0 - 1000'],
        },
      ],
    },
    applicants: {
      name: 'Applicants',
      validationType: 'array',
    },
  },
  (value, key) => ({
    aliasName: value.name,
    ...value,
    id: key,
  })
);

export default JobModel;
